import React, {
    useRef, useState, useEffect
} from 'react';
import { intlShape } from 'react-intl';
import { Grid, Box } from '@mui/material';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { List as IList } from 'immutable';
import Carousel from 'react-multi-carousel';
import Button from '@mui/material/Button';
import { HOME_STEP } from '../../routing/flowConstants';

import { getCategorySpecificFaceplates } from '../../utils/landingPageUtils';
import { getFaceplateGroups } from '../../landingPage/landingPageSelectors';
import { saveSelectedCard, selectedCardType, setClickedOnUYO } from '../../landingPage/landingPageModule';
import { flushProductDetails, E_CARD_TYPE, PHYSICAL_CARD_TYPE } from '../../productCartHandler/productCartModule';
import windowSize from '../../utils/windowSize';
import { getLoaderStatus } from '../../loader/loaderSelector';
import { getIsOldUiPreferred, getPhase2Preferred } from '../../app/bootstrap';
import GiftCardDeliveryTypeOptions from '../GiftCardDeliveryTypeOptions';

import { deviceType } from '../../utils/deviceTypeUtils';
import {
    responsiveCarouselV2, setCarouselTransform,
    setInitialCarouselTransform, setImageWidthAndMargin
} from './CarouselConfig';
import { trackButtonClick } from '../../segment/segmentModule';

import GiftCardDeliveryOptionCarousel from '../GiftCardDeliveryOptionCarousel';
import { cardDeliveryTypeOptionsList } from '../CardDeliveryTypeOptionsList';
import SiteSwitchDialog from '../../localeCurrencyPicker/SiteSwitchDialog';
import { getCartMaxNumPlasticCards } from '../../brand/brandSelectors';
import { getRemainingPlasticCardsAllowed } from '../../productCartHandler/productCartSelectors';
import { CARD_TYPES } from '../../constants/constants';
import { setupCarouselArrowFocus, setupCarouselTabFocus } from '../../utils/carouselUtils';
import { setCurrency as setCurrencyActionCreator } from '../../intl/intlModule';
import { logWarning } from '../../utils/errorUtils';
import ImageWithSkeleton from '../../common/ImageWithSkeleton';

const FaceplateCarousel = (props) => {
    const {
        category, faceplates, handleSubmit, saveSelectedCardAction, flushProductDetailsAction,
        carouselKey, giftCardDeliveryType, categoryCount, setFaceplatesCount, isLoading,
        setCarouselLoaded, windowWidth, intl, cartMaxNumPlasticCards, remainingPlasticCardsAllowed, trackAddAnotherButton,
        saveSelectedCardType, tempCurrency, setCurrency, setClickedOnUYOAction, shouldSetCurrency
    } = props;

    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [isMaxPhysicalItemModalOpen, setIsMaxPhysicalItemModalOpen] = useState(false);
    const [selectedFaceplate, setSelectedFaceplate] = useState(null);

    const handleMaxLimitModalClose = value => setIsMaxPhysicalItemModalOpen(value);
    const handleMaxLimitModalOpen = () => setIsMaxPhysicalItemModalOpen(true);
    const safeCategory = category.replace(/[^a-zA-Z0-9]/g, '-');

    const handleCloseModel = value => setDialogOpen(value);
    
    const handleClickForModal = () => setDialogOpen(true);

    const sortedCardDeliveryTypeOptionsList = cardDeliveryTypeOptionsList(intl, giftCardDeliveryType);
    const [additionalTransfrom, setadditionalTransfrom] = useState(0);
    const carouselRef = useRef();
    const prevSlideNumber = useRef(0);
    const carouselEndReached = useRef(false);
    const device = deviceType(windowWidth);

    useEffect(() => {
        setInitialCarouselTransform(windowWidth, faceplates.size, setadditionalTransfrom);
    }, [faceplates, windowWidth]);

    const physicalCardMaxLimitDigitalButtonClick = () => {
        saveSelectedCardAction(category, remainingPlasticCardsAllowed === 0 ? faceplates.get(0) : selectedFaceplate, true, giftCardDeliveryType === PHYSICAL_CARD_TYPE);
        handleSubmit(false);
    };

    const faceplateOnClick = (faceplate) => {
        saveSelectedCardAction(category, faceplate, true, giftCardDeliveryType === PHYSICAL_CARD_TYPE);

        setSelectedFaceplate(faceplate);
        const isPhysicalCardType = giftCardDeliveryType === PHYSICAL_CARD_TYPE;
        saveSelectedCardType(isPhysicalCardType ? CARD_TYPES.Physical : CARD_TYPES.Digital);
        if (isPhysicalCardType && remainingPlasticCardsAllowed === 0) {
            handleMaxLimitModalOpen();
            return;
        }

        if (getIsOldUiPreferred()) {
            flushProductDetailsAction();
        }
        if (shouldSetCurrency) {
            setCurrency(tempCurrency);
            setClickedOnUYOAction(false);
            handleSubmit(isPhysicalCardType);
        }
    };
    useEffect(() => {
        setFaceplatesCount(faceplates.size);
    }, [faceplates.size]);

    const [loadedImages, setLoadedImages] = useState(0);
    const totalImages = faceplates.size; 

    const handleImageLoad = () => {
        setLoadedImages(prev => prev + 1);
    };

    useEffect(() => {
        if (loadedImages === totalImages) {
            setCarouselLoaded(true);
            setLoadedImages(0);
        }
    }, [loadedImages, totalImages]);

    
    useEffect(() => {
        const cleanup = setupCarouselArrowFocus(300); 
        return cleanup;
    }, [category, giftCardDeliveryType]);

    
    const arrowKeyNavRef = useRef(false);
    useEffect(() => {
        setupCarouselTabFocus(arrowKeyNavRef);
    }, [category, giftCardDeliveryType]);

    useEffect(() => {
        function updateWidths() {
            const containerSelector = `.carousel-container-${safeCategory}.${giftCardDeliveryType === PHYSICAL_CARD_TYPE
                ? 'physical-carousel-container'
                : 'digital-carousel-container'
            }`;
            const container = document.querySelector(containerSelector);
            if (!container) return;
            const containerWidth = container.offsetWidth;
            let deviceConfig = device;
            if (device === 'tab') {
                deviceConfig = 'tablet';
            } else if (!responsiveCarouselV2[deviceConfig]) {
                deviceConfig = 'desktop'; 
            }

            const config = responsiveCarouselV2[deviceConfig];
            const { items } = config;
            const gutter = config.partialVisibilityGutter || 0;

            setTimeout(() => {
                const itemsList = container.querySelectorAll('.react-multi-carousel-item.image-item');
                if (itemsList && itemsList.length <= items) {
                    let dynamicWidth = (containerWidth - gutter) / items;
                    if (device === 'desktop') {
                        dynamicWidth -= 37;
                    }
                    itemsList.forEach((li) => {
                        li.style.width = `${dynamicWidth}px`;
                    });
                }
            }, 0);
        }

        updateWidths();
        window.addEventListener('resize', updateWidths);
        return () => window.removeEventListener('resize', updateWidths);
    }, [category, faceplates.size, device, windowWidth, giftCardDeliveryType]);

    return (
        <>
            {(faceplates.size > 1 || categoryCount > 1) ? (
                <Carousel
                    transitionDuration={500}
                    key={carouselKey}
                    ref={carouselRef}
                    partialVisible
                    itemClass={`image-item ${!shouldSetCurrency ? 'image-item-not-clickable' : ''}`}
                    responsive={responsiveCarouselV2}
                    additionalTransfrom={additionalTransfrom}
                    containerClass={
                        `carousel-container carousel-container-${safeCategory}
                        ${giftCardDeliveryType === PHYSICAL_CARD_TYPE
                    ? 'physical-carousel-container'
                    : 'digital-carousel-container'
                }`
                    }
                    beforeChange={(nextSlideNumber) => {
                        const setTransform = (forwardVal, reverseVal, endVal) => {
                            setCarouselTransform(
                                carouselRef,
                                prevSlideNumber, carouselEndReached,
                                device, faceplates.size,
                                nextSlideNumber,
                                forwardVal, reverseVal, endVal,
                                additionalTransfrom, setadditionalTransfrom
                            );
                        };

                        switch (device) {
                            case 'mobile':
                                setTransform(-16, 16, -16);
                                break;
                            case 'tab':
                                setTransform(-32, 32, -16);
                                break;
                            default:
                                setTransform(-64, 64, -16);
                                break;
                        }
                    }}
                    afterChange={(previousSlide, { currentSlide }) => {
                        setTimeout(() => {
                            try {
                                if (device !== 'mobile') {
                                    const direction = currentSlide > previousSlide ? 'Right' : 'Left';
                                    const cardType = giftCardDeliveryType === PHYSICAL_CARD_TYPE ? CARD_TYPES.Physical : CARD_TYPES.Digital;
                                    trackAddAnotherButton(
                                        `${cardType} Carousel ${direction} Arrow Click`,
                                        HOME_STEP,
                                        'Homepage Carousel Arrow Click'
                                    );
                                }
                            } catch (err) {
                                logWarning('Homepage carousel arrow tracking failed', { error: err.message });
                            }
                            
                            if (!arrowKeyNavRef.current) return;
                            arrowKeyNavRef.current = false; 

                            
                            const container = giftCardDeliveryType === PHYSICAL_CARD_TYPE
                                ? document.querySelector('.physical-carousel-container')
                                : document.querySelector('.digital-carousel-container');

                            if (!container) return;

                            const visibleSlides = container.querySelectorAll(
                                '.react-multi-carousel-item--active:not(.react-multi-carousel-item--cloned)'
                            );

                            if (visibleSlides.length > 0) {
                                const firstSlide = visibleSlides[0];
                                const focusable = firstSlide.querySelector(
                                    '[tabindex], a, button, input, [role="button"]'
                                );
                                
                                if (focusable) focusable.focus();
                                else firstSlide.focus();
                            }
                        }, 0); 
                    }}
                >
                    {faceplates.map((faceplate, index) => {
                        const dimensions = setImageWidthAndMargin(
                            faceplates.size, windowWidth, index, device
                        );
                        return (
                            <Button
                                onClick={() => faceplateOnClick(faceplate)}
                                key={faceplate.get('code')}
                                className={`faceplate-wrapper ${!shouldSetCurrency ? 'faceplate-wrapper-not-clickable' : ''}`}
                                style={dimensions}
                                sx={{
                                    minHeight: dimensions.height
                                }}
                                disabled={!shouldSetCurrency}
                            >
                                <ImageWithSkeleton
                                    className='faceplates'
                                    src={faceplate.get('imageURL')}
                                    alt={faceplate.get('altText')}
                                    onLoad={handleImageLoad}
                                    skeletonSx={{ aspectRatio: '105/63.1' }}
                                />
                            </Button>
                        );
                    })}
                </Carousel>
            ) : (
                <Box>
                    <Grid
                        container
                    >
                        <Grid
                            item
                            xs={12}
                            sm={8}
                            sx={{ display: { xs: 'block', sm: 'none' } }}
                        >
                            {}
                            <GiftCardDeliveryOptionCarousel
                                cardDeliveryTypeOptionsList={sortedCardDeliveryTypeOptionsList}
                                isLoading={isLoading}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        className='choose-a-design-container'
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Grid item xs={12} sm={4} md={3} lg={3}>
                            {faceplates.map(faceplate => (
                                <Button
                                    onClick={() => {
                                                                                if (giftCardDeliveryType === E_CARD_TYPE) {
                                            faceplateOnClick(faceplate);
                                        }
                                                                                if (giftCardDeliveryType === PHYSICAL_CARD_TYPE && !getPhase2Preferred()) {
                                            handleClickForModal();
                                        } else {
                                            faceplateOnClick(faceplate);
                                        }
                                    }}
                                    key={faceplate.get('code')}
                                    className={`faceplate-wrapper ${!shouldSetCurrency ? 'faceplate-wrapper-not-clickable' : ''}`}
                                    disabled={!shouldSetCurrency}
                                >
                                    <ImageWithSkeleton
                                        className='faceplates single-faceplate'
                                        src={faceplate.get('imageURL')}
                                        alt={faceplate.get('altText')}
                                        skeletonSx={{ aspectRatio: '105/63.1' }}
                                        onLoad={handleImageLoad}
                                    />
                                </Button>
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={8} md={9} lg={9} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {}
                            <GiftCardDeliveryTypeOptions
                                giftCardDeliveryType={giftCardDeliveryType}
                                faceplatesCount={faceplates.size}
                                isLoading={isLoading}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
            {isDialogOpen && (
                <SiteSwitchDialog
                    physicalCard
                    isOpen={isDialogOpen}
                    handleCloseModel={handleCloseModel}
                />
            )}
            {isMaxPhysicalItemModalOpen && (
                <SiteSwitchDialog
                    physicalCard
                    isOpen={isMaxPhysicalItemModalOpen}
                    handleCloseModel={handleMaxLimitModalClose}
                    isPhysicalcartMaxlimitReached={isMaxPhysicalItemModalOpen}
                    onConfirmCTA={() => physicalCardMaxLimitDigitalButtonClick()
                    }
                    cartMaxNumPlasticCards={cartMaxNumPlasticCards}
                />
            )}
        </>
    );
};

FaceplateCarousel.defaultProps = {
    cartMaxNumPlasticCards: 0
};

FaceplateCarousel.propTypes = {
    category: PropTypes.string.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    saveSelectedCardAction: PropTypes.func.isRequired,
    flushProductDetailsAction: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    faceplates: PropTypes.instanceOf(IList).isRequired,
    carouselKey: PropTypes.number.isRequired,
    giftCardDeliveryType: PropTypes.string.isRequired,
    categoryCount: PropTypes.number.isRequired,
    setFaceplatesCount: PropTypes.func.isRequired,
    setCarouselLoaded: PropTypes.func.isRequired,
    windowWidth: PropTypes.number.isRequired,
    intl: intlShape.isRequired,
    cartMaxNumPlasticCards: PropTypes.number,
    remainingPlasticCardsAllowed: PropTypes.number.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    tempCurrency: PropTypes.string.isRequired,
    setCurrency: PropTypes.func.isRequired,
    setClickedOnUYOAction: PropTypes.func.isRequired,
    shouldSetCurrency: PropTypes.bool.isRequired,
    trackAddAnotherButton: PropTypes.func.isRequired
};

const mapStateToProps = (state, { category, giftCardDeliveryType }) => {
    const faceplateGroups = getFaceplateGroups(state);
    const faceplates = getCategorySpecificFaceplates(faceplateGroups, category,
        giftCardDeliveryType === PHYSICAL_CARD_TYPE);
    const isLoading = getLoaderStatus(state);
    const cartMaxNumPlasticCards = getCartMaxNumPlasticCards(state);
    const remainingPlasticCardsAllowed = getRemainingPlasticCardsAllowed(state);

    return {
        faceplates,
        isLoading,
        cartMaxNumPlasticCards,
        remainingPlasticCardsAllowed
    };
};

const mapDispatchToProps = {
    saveSelectedCardAction: saveSelectedCard,
    flushProductDetailsAction: flushProductDetails,
    saveSelectedCardType: selectedCardType,
    setCurrency: setCurrencyActionCreator,
    setClickedOnUYOAction: setClickedOnUYO,
    trackAddAnotherButton: trackButtonClick
};

export default connect(mapStateToProps, mapDispatchToProps)(windowSize(FaceplateCarousel));
